import { requireCompletedProfile } from "@hisani/auth";
import { canRequestOwnerLink } from "../domain/membership/authorization-policy.js";
import { normalizeContactPointValue } from "../domain/membership/contact-point-normalization.js";
import { cancelOwnerLinkRequestSchema, changePrimaryOwnerSchema, listOwnerLinksForHorseSchema, requestOwnerLinkSchema, respondOwnerLinkRequestSchema, searchHorsemenSchema, stableConflict, stableForbidden, stableNotFound, } from "../validation.js";
import { requireFeatureMembership } from "./sealed-record-support.js";
const FEATURE = "LINK_AN_OWNER";
const SEARCH_RESULT_LIMIT = 20;
export async function searchHorsemen(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = searchHorsemenSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    return dependencies.repository.searchHorsemen({
        query: parsed.query,
        limit: SEARCH_RESULT_LIMIT,
    });
}
export async function requestOwnerLink(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = requestOwnerLinkSchema.parse(input);
    const membership = await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const target = parsed.target.kind === "HORSEMAN"
        ? { kind: "HORSEMAN", horsemanId: parsed.target.horsemanId }
        : {
            kind: "INVITE",
            contactPointType: parsed.target.contactPointType,
            normalizedContactPoint: normalizeContactPointValue(parsed.target.contactPointType, parsed.target.contactPointValue),
        };
    const commandFingerprint = dependencies.identifierFingerprint.fingerprint([
        "OWNER_LINK_REQUEST",
        parsed.stableId,
        parsed.horseId,
        target.kind,
        target.kind === "HORSEMAN" ? target.horsemanId : target.contactPointType,
        target.kind === "INVITE" ? target.normalizedContactPoint : "",
    ].join("\u001f"));
    const replay = await dependencies.repository.replayOwnerLinkRequest({
        actorId: actor.account.id,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint,
    });
    if (replay?.outcome === "REPLAYED")
        return replay.request;
    if (replay?.outcome === "IDEMPOTENCY_CONFLICT") {
        throw stableConflict("This mutation identifier was already used for a different Owner Link command.");
    }
    const dashboard = await dependencies.repository.getStableDashboard(parsed.stableId, actor.account.id);
    if (dashboard === null)
        throw stableNotFound();
    if (!canRequestOwnerLink(membership, dashboard.stable.subscriptionTier)) {
        throw stableForbidden("Link An Owner requires the Stable's paid subscription.");
    }
    const horse = await dependencies.repository.getHorse(parsed.stableId, parsed.horseId);
    if (horse === null)
        throw stableNotFound();
    const now = dependencies.clock.now();
    const policy = await dependencies.policy.get();
    const result = await dependencies.repository.requestOwnerLink({
        id: dependencies.newId(),
        stableId: parsed.stableId,
        horseId: parsed.horseId,
        requestedByHorsemanId: actor.account.id,
        target,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint,
        now,
        expiresAt: new Date(now.getTime() + policy.ownerLinkRequestExpiryDays * 24 * 60 * 60 * 1000),
    });
    if (result.outcome === "ALREADY_PENDING") {
        throw stableConflict("An Owner Link request is already pending for this Horseman on this horse.");
    }
    if (result.outcome === "ALREADY_LINKED") {
        throw stableConflict("This Horseman is already an accepted owner of the horse.");
    }
    if (result.outcome === "HORSEMAN_NOT_FOUND" ||
        result.outcome === "HORSE_NOT_FOUND") {
        throw stableNotFound();
    }
    if (result.outcome === "IDEMPOTENCY_CONFLICT") {
        throw stableConflict("This mutation identifier was already used for a different Owner Link command.");
    }
    return result.request;
}
export async function listOwnerLinkRequestsForHorse(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listOwnerLinksForHorseSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    return dependencies.repository.listOwnerLinkRequestsForHorse(parsed.stableId, parsed.horseId);
}
export async function listHorseOwnerLinks(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listOwnerLinksForHorseSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    return dependencies.repository.listHorseOwnerLinks(parsed.stableId, parsed.horseId);
}
export async function cancelOwnerLinkRequest(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = cancelOwnerLinkRequestSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const now = dependencies.clock.now();
    const outcome = await dependencies.repository.cancelOwnerLinkRequest({
        stableId: parsed.stableId,
        requestId: parsed.requestId,
        actorId: actor.account.id,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint: dependencies.identifierFingerprint.fingerprint([
            "OWNER_LINK_CANCEL",
            parsed.stableId,
            parsed.requestId,
            parsed.expectedVersion,
        ].join("\u001f")),
        expectedVersion: parsed.expectedVersion,
        now,
    });
    if (outcome === "NOT_FOUND")
        throw stableNotFound();
    if (outcome === "STALE_VERSION") {
        throw stableConflict("This Owner Link request has changed. Refresh and retry.");
    }
    if (outcome === "IDEMPOTENCY_CONFLICT") {
        throw stableConflict("This mutation identifier was already used for a different Owner Link command.");
    }
    return { cancelled: true };
}
/**
 * The named Horseman's accept/decline response. Deliberately authorized by
 * identity alone (the request's own `ownerHorsemanId`, checked inside
 * `respondOwnerLinkRequest`'s repository transaction) — never a Stable
 * permission check, since the named Horseman "may not be a member of the
 * requesting Stable at all" (`docs/products/stable.md`).
 */
export async function respondOwnerLinkRequest(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = respondOwnerLinkRequestSchema.parse(input);
    const now = dependencies.clock.now();
    const result = await dependencies.repository.respondOwnerLinkRequest({
        requestId: parsed.requestId,
        actorId: actor.account.id,
        decision: parsed.decision,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint: dependencies.identifierFingerprint.fingerprint([
            "OWNER_LINK_RESPOND",
            parsed.requestId,
            parsed.decision,
            parsed.expectedVersion,
        ].join("\u001f")),
        expectedVersion: parsed.expectedVersion,
        now,
    });
    if (result === null) {
        throw stableConflict("This Owner Link request is no longer pending, expired, or does not name you.");
    }
    if (!("request" in result)) {
        throw stableConflict(result.outcome === "STALE_VERSION"
            ? "This Owner Link request has changed. Refresh and retry."
            : "This mutation identifier was already used for a different Owner Link command.");
    }
    return result;
}
export async function changePrimaryOwner(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = changePrimaryOwnerSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const now = dependencies.clock.now();
    const result = await dependencies.repository.changePrimaryOwner({
        stableId: parsed.stableId,
        horseId: parsed.horseId,
        horsemanId: parsed.horsemanId,
        actorId: actor.account.id,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint: dependencies.identifierFingerprint.fingerprint([
            "OWNER_LINK_CHANGE_PRIMARY",
            parsed.stableId,
            parsed.horseId,
            parsed.horsemanId,
            parsed.expectedOwnerLinksVersion,
        ].join("\u001f")),
        expectedOwnerLinksVersion: parsed.expectedOwnerLinksVersion,
        now,
    });
    if (result.outcome === "NOT_FOUND")
        throw stableNotFound();
    if (result.outcome === "STALE_VERSION") {
        throw stableConflict("The horse's Owner Links changed. Refresh and retry.");
    }
    if (result.outcome === "IDEMPOTENCY_CONFLICT") {
        throw stableConflict("This mutation identifier was already used for a different Owner Link command.");
    }
    if (result.outcome === "CHANGED" || result.outcome === "REPLAYED") {
        return result.link;
    }
    throw stableConflict("The Primary Owner could not be changed.");
}
/** The Horseman-facing incoming-requests surface — reachable regardless of
 * Stable membership, so this reads by identity only, with no `stableId`. */
export async function listOwnerLinkRequestsForHorseman(principal, dependencies) {
    const actor = requireCompletedProfile(principal);
    return dependencies.repository.listOwnerLinkRequestsForHorseman(actor.account.id);
}
export async function listAcceptedOwnerLinksForHorseman(principal, dependencies) {
    const actor = requireCompletedProfile(principal);
    return dependencies.repository.listAcceptedOwnerLinksForHorseman(actor.account.id);
}
